import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { useMemo, useState } from 'react';

import { Modal } from '@/components/Modal';
import { RemovableDashedTag } from '@/components/RemovableDashedTag';
import { useStatisticsBatch } from '@/hooks/useStatisticsBatch';
import { cn } from '@/utils/classname';

export type TagEntity = 'asset' | 'risk';

export const AddTag = ({
  open,
  onClose,
  disabled,
  description = 'Tags help organize and classify assets in your attack surface.',
  onSubmit,
  entity = 'asset',
}: {
  open: boolean;
  onClose: () => void;
  disabled: boolean;
  description?: string;
  onSubmit: (tag: string) => void;
  /**
   * Which entity's existing tag statistics to surface as suggestions.
   * Asset and risk services persist a `#statistic#<entity>#tags#now#<tag>`
   * record on every PUT that includes tags, and the dialog reads that list
   * here so the user can pick an existing tag or create a new one.
   */
  entity?: TagEntity;
}) => {
  const [tagValue, setTagValue] = useState('');

  const statisticKey = `${entity}#tags`;
  const { data: statsByKey, isLoading } = useStatisticsBatch({
    statisticKeys: [statisticKey],
    enabled: open,
  });

  const existingTags = useMemo<string[]>(() => {
    const stats = statsByKey?.[statisticKey] ?? [];
    const seen = new Set<string>();
    for (const stat of stats) {
      if (!stat.value || seen.has(stat.value)) continue;
      seen.add(stat.value);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [statsByKey, statisticKey]);

  const trimmed = tagValue.trim();
  const lowerTrimmed = trimmed.toLowerCase();

  const filteredTags = useMemo(() => {
    if (!trimmed) return existingTags;
    return existingTags.filter(t => t.toLowerCase().includes(lowerTrimmed));
  }, [existingTags, trimmed, lowerTrimmed]);

  const hasExactMatch = existingTags.some(
    t => t.toLowerCase() === lowerTrimmed
  );
  const showCreateOption = trimmed.length > 0 && !hasExactMatch;

  function reset() {
    setTagValue('');
    onClose();
  }

  return (
    <Modal
      title="Add Tag"
      open={open}
      onClose={onClose}
      footer={{
        text: 'Add',
        form: 'custom-tag-form',
        disabled: disabled || !trimmed,
      }}
    >
      <p className="text-chariot-primary mb-6">{description}</p>
      <form
        id="custom-tag-form"
        className="space-y-4"
        onSubmit={async event => {
          event.preventDefault();
          if (!trimmed) return;
          await onSubmit(trimmed);
          reset();
        }}
      >
        <Combobox
          immediate
          value={tagValue}
          onChange={(next: string | null) => {
            if (next !== null) setTagValue(next);
          }}
        >
          <label
            htmlFor="custom-tag-combobox-input"
            className="text-chariot-primary mb-1 block text-sm font-medium"
          >
            Tag
          </label>
          <div className="relative">
            <ComboboxInput
              id="custom-tag-combobox-input"
              className={cn(
                'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
                'placeholder:text-chariot-text-tertiary w-full rounded-md border px-3 py-2 text-sm',
                'focus:border-chariot-brand-primary focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden'
              )}
              displayValue={(v: string) => v}
              placeholder="production"
              autoComplete="off"
              autoFocus
              onChange={e => setTagValue(e.target.value)}
            />
            <ComboboxOptions
              className={cn(
                'bg-chariot-background-secondary border-chariot-stroke absolute z-50 mt-1',
                'max-h-60 w-full overflow-auto rounded-md border shadow-lg',
                'empty:hidden'
              )}
            >
              {showCreateOption && (
                <ComboboxOption
                  value={trimmed}
                  className="data-focus:bg-chariot-background-tertiary hover:bg-chariot-background-tertiary flex cursor-pointer items-center gap-2 px-3 py-2"
                >
                  <span className="text-chariot-text-primary text-sm">
                    Create new tag:{' '}
                    <span className="font-semibold">{trimmed}</span>
                  </span>
                </ComboboxOption>
              )}
              {isLoading ? (
                <div className="text-chariot-text-secondary px-3 py-2 text-sm">
                  Loading existing tags…
                </div>
              ) : filteredTags.length === 0 && !showCreateOption ? (
                <div className="text-chariot-text-secondary px-3 py-2 text-sm">
                  {existingTags.length === 0
                    ? 'No tags yet — type to create one.'
                    : 'No matching tags.'}
                </div>
              ) : (
                filteredTags.map(tag => (
                  <ComboboxOption
                    key={tag}
                    value={tag}
                    className="data-focus:bg-chariot-background-tertiary hover:bg-chariot-background-tertiary flex cursor-pointer items-center gap-2 px-3 py-2"
                  >
                    <span className="text-chariot-text-primary truncate text-sm">
                      {tag}
                    </span>
                  </ComboboxOption>
                ))
              )}
            </ComboboxOptions>
          </div>
        </Combobox>
      </form>
    </Modal>
  );
};

export const SimpleTags = ({
  tags,
  onRemove,
  variant = 'default',
}: {
  tags: string[];
  onRemove?: (tag: string) => void;
  variant?: 'default' | 'compact';
}) => {
  if (tags.length === 0) return null;

  // Compact variant for drawer headers - smaller, solid Chariot purple
  if (variant === 'compact') {
    return (
      <div
        className="flex flex-wrap gap-2"
        role="list"
        aria-label="Custom tags"
      >
        {tags?.map((tag, index) =>
          onRemove ? (
            <RemovableDashedTag
              key={`${tag}-${index}`}
              label={tag}
              size="xs"
              onRemove={() => onRemove(tag)}
              className="border-chariot-brand-primary bg-chariot-brand-primary hover:bg-chariot-brand-primary/80 hover:border-chariot-brand-primary/80 border-solid text-white"
            />
          ) : (
            <span
              key={`${tag}-${index}`}
              role="listitem"
              className="border-chariot-brand-primary bg-chariot-brand-primary inline-flex items-center rounded-full border-2 border-solid px-2 py-1 text-[10px] leading-3 font-semibold text-white uppercase"
            >
              {tag}
            </span>
          )
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className="flex flex-wrap gap-2" role="list" aria-label="Custom tags">
      {tags?.map((tag, index) =>
        onRemove ? (
          <RemovableDashedTag
            key={`${tag}-${index}`}
            label={tag}
            size="sm"
            onRemove={() => onRemove(tag)}
          />
        ) : (
          <span
            key={`${tag}-${index}`}
            role="listitem"
            className="border-chariot-stroke text-chariot-text-secondary inline-flex items-center rounded-full border-2 border-dashed px-3 py-1.5 text-xs leading-3 font-semibold uppercase"
          >
            {tag}
          </span>
        )
      )}
    </div>
  );
};
